import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Button,
  Drawer,
  Avatar,
  Badge,
  CardActionArea,
  Typography,
  Tooltip,
  Chip
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import '../../Style/Pulse.css'
import {
  AddComment,
  Check,
  ContactSupport,
  Forum,
  Fullscreen,
  KeyboardDoubleArrowDown
} from '@mui/icons-material'
import PulseComments from './PulseComment'
import RTEView from '../RichTextEditor/RTEView'
import InfoDialog from './InfoDialog'
import PulseAlertMessage from './Helpers/PulseAlertMessage'

const ABTesting = ({
  section,
  comments = [],
  setComments,
  isAllowedToComment,
  displayName,
  uid,
  document,
  sectionIndex,
  pulseIndex,
  setFullScreenImageSwitch,
  goToNextSection,
  hasNextSection,
  isPreview
}) => {
  const scrollRef = useRef()
  const [pollAnswers, setPollAnswers] = useState(
    section.pulseOptions.map(value => {
      return {
        option: value.text,
        votes: 0
      }
    })
  )
  const [userVoted, setUserVoted] = useState(null)
  const [alert, setAlert] = useState(null)
  const [currentComment, setCurrentComment] = useState({
    message: '',
    messageType: 'Vote',
    uid,
    displayName
  })
  const [isAdmin, setIsAdmin] = useState(false)
  const [sortType, setSortType] = useState('All Comments')
  const [open, setOpen] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [loading, setLoading] = useState(false)
  const [clickedOption, setClickedOption] = useState(null)

  const toggleDrawer = open => event => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }
    if (!open) {
      setShowEditModal(false)
    }
    setOpen(open)
  }

  const addComment = (comment, answer) => {
    if (!isAllowedToComment) {
      console.warn('User is not allowed to comment.')
      return
    }
    const updatedAnswers = pollAnswers.map(option => {
      if (option.option === answer) {
        return { ...option, votes: option.votes + 1 }
      }
      return option
    })
    if (userVoted) {
      updatedAnswers.forEach(option => {
        if (option.option === userVoted) {
          option.votes -= 1
        }
      })
    }
    comment.id = `${uid}-${Date.now()}`
    comment.sectionIndex = sectionIndex
    comment.option = answer
    const d = new Date()
    comment.time = d
    comment.dateString = d.toDateString()
    setUserVoted(answer)
    setPollAnswers(updatedAnswers)
    setComments([...comments, comment], comment)
    setCurrentComment({
      message: '',
      messageType: 'Comment',
      uid,
      displayName
    })
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 250)
  }

  useEffect(() => {
    setIsAdmin(
      document &&
        (document.uid === uid ||
          (document.admins && document.admins.includes(uid)))
    )
  }, [document, uid])

  useEffect(() => {
    let commentsArray = comments.filter(
      comment =>
        comment.sectionIndex === sectionIndex &&
        comment.pulseIndex === pulseIndex
    )
    setPollAnswers(
      section.pulseOptions.map(value => {
        return {
          option: value.text,
          votes: commentsArray.reduce((acc, curr) => {
            return curr.option === value.text ? acc + 1 : acc
          }, 0)
        }
      })
    )
    setUserVoted(
      commentsArray.find(comment => comment.uid === uid)?.option || null
    )
  }, [comments])

  const handleVote = answer => {
    if (!isAllowedToComment) {
      return
    }
    if (userVoted) {
      setAlert(
        'You have already responded, please remove your response to respond differently.'
      )
      return
    }
    setAlert(null)
    addComment(currentComment, answer)
  }

  const saveVote = valueToSave => {
    setClickedOption(valueToSave)
    if (!loading) {
      setLoading(true)
      setTimeout(() => {
        handleVote(valueToSave)
        setSortType('All Comments')
        setClickedOption(null)
        setLoading(false)
      }, 650)
    }
  }

  const VotingSection = ({}) => {
    return (
      <Stack
        spacing={{ xs: 1, md: 2 }}
        direction='row'
        justifyContent='center'
        useFlexGap
        sx={{
          flexWrap: 'wrap',
          pb: { xs: 1, md: 2 },
          opacity:
            (isAdmin && isPreview && !isAllowedToComment) || !isAllowedToComment
              ? 0.6
              : 1,
          pointerEvents:
            ((isAdmin && isPreview && !isAllowedToComment) ||
              !isAllowedToComment) &&
            'none'
        }}
        onClick={
          (isAdmin && isPreview && !isAllowedToComment) || !isAllowedToComment
            ? e => {
                e.stopPropagation()
              }
            : null
        }
      >
        {section.pulseOptions.map((option, optionIndex) => (
          <Card
            raised
            key={optionIndex + '_option' + sectionIndex + '_section'}
            className='abTestingCard'
            sx={{ width: 'min(100%, 350px)' }}
          >
            <CardActionArea
              sx={{
                height: '100%',
                '&:focus': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText'
                },
                backgroundColor:
                  clickedOption === option.text && 'primary.main',
                color: clickedOption === option.text && 'primary.contrastText'
              }}
              onClick={() => saveVote(option.text)}
            >
              <CardContent>
                {document.optionImages &&
                  document.optionImages[
                    'sec' + sectionIndex + '_opt' + optionIndex
                  ] && (
                    <Box className='previewHolder' sx={{ mb: 2 }}>
                      <Badge
                        badgeContent={
                          <IconButton
                            onClick={e => {
                              e.stopPropagation()
                              setFullScreenImageSwitch(
                                document.optionImages[
                                  'sec' + sectionIndex + '_opt' + optionIndex
                                ]
                              )
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.stopPropagation()
                                setFullScreenImageSwitch(
                                  document.optionImages[
                                    'sec' + sectionIndex + '_opt' + optionIndex
                                  ]
                                )
                              }
                            }}
                          >
                            <Fullscreen />
                          </IconButton>
                        }
                        className='abImageFullscreen'
                      >
                        <Avatar
                          sx={{
                            width: 'auto',
                            height: 'auto',
                            maxHeight: { md: 200, xs: 150 },
                            maxWidth: { md: 200, xs: 150 }
                          }}
                          loading='lazy'
                          variant='rounded'
                          className='previewFrame'
                          alt={option.alt || 'Option ' + optionIndex + ' Image'}
                          src={
                            document.optionImages[
                              'sec' + sectionIndex + '_opt' + optionIndex
                            ]
                          }
                        />
                      </Badge>
                    </Box>
                  )}
                <Typography variant='span'>
                  <RTEView content={option.text} />
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    )
  }

  const VotedSection = ({}) => {
    return (
      <Stack
        spacing={2}
        sx={{
          width: '100%',
          justifyContent: 'center',
          flexWrap: { md: 'nowrap', xs: 'wrap' },
          pb: 2,
          pt: 1
        }}
        ref={scrollRef}
      >
        {section.pulseOptions.map((option, optionIndex) => (
          <React.Fragment
            key={optionIndex + '_option' + sectionIndex + '_section'}
          >
            <Card
              raised
              sx={{
                position: 'relative',
                overflow: 'hidden',
                width: 'min(100%, 1000px)',
                margin: '8px auto !important'
              }}
              tabIndex={0}
              className='abTestingCard'
              onClick={() => userVoted && handleVote()}
              aria-labelledby={
                optionIndex + '_option' + sectionIndex + '_section_answer'
              }
            >
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '2.5%',
                  height: '12px',
                  width: `95%`,
                  borderRadius: '16px',
                  backgroundColor: 'background.default',
                  zIndex: 0,
                  transition: 'width 0.5s ease'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '2.5%',
                  height: '12px',
                  borderRadius: '16px',
                  width: `${
                    userVoted || isAdmin
                      ? (pollAnswers.reduce(
                          (acc, curr) =>
                            acc +
                            (curr.option === option.text ? curr.votes : 0),
                          0
                        ) /
                          pollAnswers.reduce(
                            (acc, curr) => acc + curr.votes,
                            0
                          )) *
                          95 || -1
                      : -1
                  }%`,
                  backgroundColor: 'primary.main',
                  zIndex: 0,
                  transition: 'width 0.5s ease'
                }}
              />
              <CardContent
                sx={{
                  display: 'flex',
                  gap: 2,
                  zIndex: 2,
                  flexWrap: { md: 'nowrap', xs: 'wrap' },
                  rowGap: 1
                }}
              >
                <div className='previewHolder'>
                  {document.optionImages &&
                    document.optionImages[
                      'sec' + sectionIndex + '_opt' + optionIndex
                    ] && (
                      <Avatar
                        sx={{
                          width: 'auto',
                          height: 'auto',
                          maxWidth: 100,
                          maxHeight: 100
                        }}
                        loading='lazy'
                        variant='rounded'
                        className='previewFrame pointer'
                        alt={option.alt || 'Option ' + optionIndex + ' Image'}
                        title={'pulse ' + document.fileName + document.uid}
                        src={
                          document.optionImages[
                            'sec' + sectionIndex + '_opt' + optionIndex
                          ]
                        }
                        onClick={e => {
                          e.stopPropagation()
                          setFullScreenImageSwitch(
                            document.optionImages[
                              'sec' + sectionIndex + '_opt' + optionIndex
                            ]
                          )
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.stopPropagation()
                            setFullScreenImageSwitch(
                              document.optionImages[
                                'sec' + sectionIndex + '_opt' + optionIndex
                              ]
                            )
                          }
                        }}
                      />
                    )}
                </div>
                <Stack
                  id={
                    optionIndex + '_option' + sectionIndex + '_section_answer'
                  }
                  direction={'row'}
                  justifyContent='space-between'
                  alignItems='baseline'
                  sx={{ flexWrap: 'wrap', width: '100%' }}
                  columnGap={2}
                >
                  <Typography sx={{ zIndex: 2 }} type='h4'>
                    <RTEView content={option.text} />
                  </Typography>
                  {(userVoted || isAdmin) && (
                    <Typography
                      type='span'
                      sx={{ alignSelf: 'flex-end', ml: 'auto' }}
                    >
                      {userVoted === option.text && (
                        <Check sx={{ top: '6px', position: 'relative' }} />
                      )}
                      {(pollAnswers.find(item => item.option === option.text)
                        .votes || 0) +
                        ' response' +
                        ((pollAnswers.find(item => item.option === option.text)
                          .votes || 0) === 1
                          ? ''
                          : 's') +
                        (pollAnswers.reduce(
                          (acc, curr) => acc + curr.votes,
                          0
                        ) > 0
                          ? ' (' +
                            Math.floor(
                              ((pollAnswers.find(
                                item => item.option === option.text
                              ).votes || 0) /
                                pollAnswers.reduce(
                                  (acc, curr) => acc + curr.votes,
                                  0
                                )) *
                                100
                            ) +
                            '%)'
                          : '')}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </React.Fragment>
        ))}
      </Stack>
    )
  }

  return (
    <>
      {alert && (
        <PulseAlertMessage
          setAlert={setAlert}
          setOpen={setOpen}
          setSortType={setSortType}
          alert={alert}
        />
      )}
      {!userVoted && (
        <Stack
          direction='row'
          justifyContent='flex-end'
          alignItems={'center'}
          sx={{ mb: 1, mt: -1 }}
        >
          <Tooltip title='Pulse Section Type'>
            <Chip
              color='secondary'
              label='A/B Testing'
              onClick={() => setShowInfo(true)}
            />
          </Tooltip>
          <Tooltip title='Pulse Usage Directions'>
            <IconButton
              sx={{ placeSelf: 'end' }}
              color='secondary'
              onClick={() => setShowInfo(true)}
            >
              <ContactSupport />
            </IconButton>
          </Tooltip>
          <InfoDialog
            showInfo={showInfo}
            setShowInfo={setShowInfo}
            showSpecificSection={'ABTesting'}
          />
        </Stack>
      )}
      {!userVoted && <VotingSection />}
      {(userVoted || (isAdmin && !isPreview)) && (
        <>
          <VotedSection />
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            flexWrap={'wrap'}
          >
            <Stack
              flexWrap={'wrap'}
              direction='row'
              justifyContent='flex-end'
              gap={1}
              sx={{ ml: 'auto' }}
            >
              <Tooltip title='View All Responses'>
                <Button
                  onClick={() => {
                    setSortType('All Comments')
                    setShowEditModal(false)
                    setOpen(true)
                  }}
                >
                  <Forum />
                </Button>
              </Tooltip>
              {userVoted && (
                <Tooltip title='Add or Edit Your Response'>
                  <Button
                    onClick={() => {
                      setSortType('Yours')
                      setOpen(true)
                      setShowEditModal(true)
                    }}
                    variant='contained'
                  >
                    <AddComment />
                  </Button>
                </Tooltip>
              )}
              {hasNextSection(section) && (
                <Tooltip title='Next Section'>
                  <Button onClick={() => goToNextSection(section)}>
                    <KeyboardDoubleArrowDown />
                  </Button>
                </Tooltip>
              )}
              <Drawer
                open={open}
                onClose={toggleDrawer(false)}
                anchor='right'
                sx={{
                  overflow: 'visible',
                  '& .MuiDrawer-paper': {
                    maxWidth: { xs: '100%', md: '50%' },
                    minWidth: { xs: '100%', md: '50%' }
                  }
                }}
              >
                <PulseComments
                  comments={comments}
                  setComments={setComments}
                  pulseValues={section.pulseOptions.map(opt => opt.text)}
                  isAllowedToComment={isAllowedToComment}
                  uid={uid}
                  isAdmin={isAdmin}
                  document={document}
                  sectionIndex={sectionIndex}
                  sortType={sortType}
                  setSortType={setSortType}
                  prompt={section.title}
                  setAlert={setAlert}
                  toggleDrawer={setOpen}
                  showEditModal={showEditModal}
                />
              </Drawer>
            </Stack>
          </Stack>
        </>
      )}
    </>
  )
}

export default ABTesting
